import { useEffect } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useInvalidatingMutation, useToastMutation } from '../utils/mutation';
import { onAngularEvent } from '../../services/angular-bridge';
import { notifySuccess } from '../../services/notify';

export interface BatchUser {
  id: string;
  name: string;
}

export interface Batch {
  id: string;
  name: string;
  numReviewed: number;
  numNotReviewed: number;
  totalItems: number;
  users: BatchUser[];
  roles: { name: string }[];
  status: string | null;
}

export interface BatchPage {
  totalBatches: number;
  totalItems: number;
  totalReviewed: number;
  totalNotReviewed: number;
  batches: Batch[];
}

// How often to re-poll the batch list. Batches are created from the Angular
// review/search grid (outside React Query's cache), and their review-status
// counts change as reviewers work — so we can't rely on invalidation alone.
const BATCH_POLL_MS = 15_000;

export function useProjectBatches(projectId: string, page: number, limit: number, assignedOnly: boolean = false) {
  return useQuery<BatchPage>({
    queryKey: ['batches', projectId, page, limit, assignedOnly],
    queryFn: () => api.get<BatchPage>(`/project/${projectId}/batch?page=${page}&limit=${limit}&assignedOnly=${assignedOnly}`),
    enabled: !!projectId,
    // Batch data is short-lived: surface newly created batches and changing
    // review status without a manual refresh. Override the 30s global staleTime
    // so returning to / refocusing the tab refetches, and poll while visible
    // (refetchIntervalInBackground defaults false, so hidden tabs don't poll).
    staleTime: 0,
    refetchInterval: BATCH_POLL_MS,
    refetchOnWindowFocus: true,
    // Keep the current page's batches (and the pagination controls) mounted
    // while the next page loads instead of collapsing the grid between fetches.
    placeholderData: keepPreviousData,
  });
}

/**
 * FeedService.BatchingComplete carries only `{projectId, prefix}` — no batch id — so this
 * refreshes every page of the list rather than patching a row. Mounted once by
 * WebSocketBridge; batches are created from the review/search grid, so nothing about this is
 * tied to the Home tab being on screen.
 *
 * The toast moved here from Angular's BatchService along with the WS listener.
 */
export function useBatchingEvents() {
  const queryClient = useQueryClient();

  useEffect(() => {
    return onAngularEvent('FeedService.BatchingComplete', (message: { projectId?: string; prefix?: string }) => {
      notifySuccess({
        title: 'Batching Complete',
        description: `Batch: ${message?.prefix} has completed.`,
        timeout: 5000,
      });

      // Prefix match — the full key includes page/limit/assignedOnly.
      if (message?.projectId) {
        queryClient.invalidateQueries({ queryKey: ['batches', message.projectId] });
      }
    });
  }, [queryClient]);
}

export interface AssignBatchInput {
  id: string;
  keepFamiliesTogether: boolean;
  delimiter: string;
  startValue: number;
  zeroPaddingLength: number;
  filesPerBatch: number;
  users: BatchUser[];
  roles: object[];
}

export function useAssignBatch(projectId: string) {
  return useInvalidatingMutation<unknown, AssignBatchInput>(
    ['batches', projectId],
    (input) => api.post(`/project/${projectId}/batch/assign`, input),
  );
}

/**
 * Creates batches from a resource selection — the React port of Angular's
 * AddBatchModalController (POST /project/:id/batch with the full Batch config).
 * Distinct from useAssignBatch (/batch/assign = assigning an existing batch to users).
 * `resources` carries the explicit id list (selection mode 'some'); omit it for
 * mode 'all', where the server batches everything matching `filter`.
 */
export interface CreateBatchInput {
  projectId: string;
  prefix: string;
  delimiter: string;
  startValue: number;
  zeroPaddingLength: number;
  filesPerBatch: number;
  keepFamiliesTogether: boolean;
  includeRelated: boolean;
  collections: string[];
  filter: unknown;
  columnFilters?: unknown;
  resources?: string[];
  searchExpression?: unknown;
  query?: unknown;
}

export function useCreateBatch(projectId: string) {
  return useToastMutation<unknown, CreateBatchInput>(
    ['batches', projectId],
    (input) => api.post(`/project/${projectId}/batch`, input),
    {
      success: (_d, v) => ({
        title: 'Batching started successfully',
        description: `Prefix: ${v.prefix}`,
        timeout: 5000,
      }),
    }
  );
}
